/**
 * src/data/affiliates.js
 *
 * Affiliate data schema + region detection + link builder + click logger.
 * This is the single source of truth for all affiliate data in Tend.
 *
 * ZERO EXTERNAL NETWORK CALLS — all operations are pure localStorage or pure
 * computation. No fetch(), XMLHttpRequest, sendBeacon, or axios. This is a hard
 * requirement from docs/COST_ARCHITECTURE.md §3 (free-tier cost principle).
 *
 * Object.freeze applied at the top level of each exported constant to prevent
 * accidental mutation of keys. Nested objects (e.g., .amazon, .awin) are NOT
 * deeply frozen so that tests can patch them with live values (e.g., setting
 * a real tag to exercise the Amazon URL branch) without going through vi.mock.
 * This trade-off is acceptable: the freeze prevents structural changes to the
 * schema shape while allowing leaf-level test patching. See Task 2 for tests.
 *
 * Export order:
 *   1) FEATURE_SOURCES
 *   2) AFFILIATE_REGIONS
 *   3) PLANT_SUPPLIERS
 *   4) AFFILIATE_PARTNERS — Phase 36 D-06 (dormant; non-plant approved Awin merchants)
 *   5) AFFILIATE_PRODUCTS
 *   6) AFFILIATE_DISCLOSURE
 *   7) detectRegion
 *   8) buildAffiliateLink
 *   9) logAffiliateClick
 *  10) internal helpers (exposed with __ prefix for tests)
 *  11) findMatchingProducts
 *  12) buildGenericSearchUrl
 *  13) rowHasAffiliateLinks
 *  14) TROUBLE_KEYWORDS (symptom → product-keyword mapping) — Phase 23 D-11
 *  15) matchProductsInText (free-text → AFFILIATE_PRODUCTS) — Phase 23 D-11
 */

import { PRODUCT_RULES } from './products.js';
import { matchesTroubleKeyword, TROUBLE_KEYWORDS as SYMPTOM_LIST } from './trouble-keywords.js';

// ---------------------------------------------------------------------------
// 1. FEATURE_SOURCES enum (D-15)
// ---------------------------------------------------------------------------

export const FEATURE_SOURCES = Object.freeze({
  SUPPLIES: 'supplies',
  PLANTS:   'plants',
  HOME:     'home',
  SUPPORT:  'support',
  INSPIRE:  'inspire',
  TROUBLE:  'trouble',
  SUPPLIER: 'supplier',
  SHOPPING: 'shopping',
});

// Internal set for O(1) membership checks — not exported
const FEATURE_VALUES = new Set(Object.values(FEATURE_SOURCES));

// ---------------------------------------------------------------------------
// 2. AFFILIATE_REGIONS (D-01) — 4 seeded regions: GB (full), DE/FR/NL (placeholders)
//
// GB is fully structured; DE/FR/NL carry the same shape with null identifiers so
// adding a region requires only data, not code. US is not seeded — detectRegion()
// returns 'GB' for US visitors via the fallback table (see FALLBACK_TABLE below).
// Tags are null until Amazon Associates / Awin publisher accounts are approved;
// buildAffiliateLink() returns null for any region with both null tag and null
// publisherId (D-12). Real tags will hot-swap in a post-v2.1 update.
// ---------------------------------------------------------------------------

export const AFFILIATE_REGIONS = Object.freeze({
  GB: {
    currency: 'GBP',
    // Amazon Associates UK account created 2026-04-13 — must land 3 qualifying
    // purchases within 180 days for Amazon to review and approve the account.
    // If approval fails, tag becomes invalid and must be removed.
    amazon:   { tag: 'envision8-21', domain: 'amazon.co.uk', onelink: true },
    // Awin publisher account (Envision8) approved 2026-04-16 — publisher ID
    // active but individual merchant programme applications still required
    // before each supplier link will track commission.
    awin:     { publisherId: '2857813' },
    networks: ['awin', 'webgains'],
    fallback: 'skimlinks',
  },
  DE: {
    currency: 'EUR',
    amazon:   { tag: null, domain: 'amazon.de', onelink: true },
    awin:     { publisherId: null },
    networks: ['awin'],
    fallback: 'skimlinks',
  },
  FR: {
    currency: 'EUR',
    amazon:   { tag: null, domain: 'amazon.fr', onelink: true },
    awin:     { publisherId: null },
    networks: ['awin'],
    fallback: 'skimlinks',
  },
  NL: {
    currency: 'EUR',
    amazon:   { tag: null, domain: 'amazon.nl', onelink: true },
    awin:     { publisherId: null },
    networks: ['awin'],
    fallback: 'skimlinks',
  },
});

// ---------------------------------------------------------------------------
// 3. FALLBACK_TABLE (D-04) — maps browser region codes to seeded regions.
//
// Unknown codes (e.g., JP, KR, AU) fall through to DEFAULT_REGION ('GB').
// CA and US would both map to US, but US is not seeded, so they also resolve
// to DEFAULT_REGION via the conditional in detectRegion().
// ---------------------------------------------------------------------------

const FALLBACK_TABLE = Object.freeze({
  GB: 'GB', IE: 'GB',
  DE: 'DE', AT: 'DE', CH: 'DE',
  FR: 'FR', BE: 'FR', LU: 'FR',
  NL: 'NL',
  // CA→US and US→US would route to US; US is not seeded, fall through to DEFAULT_REGION.
});

const DEFAULT_REGION = 'GB';

// ---------------------------------------------------------------------------
// 4. PLANT_SUPPLIERS (D-03) — 7 Awin UK plant suppliers, status-gated
//
// Merchant IDs are real per docs/AFFILIATE-RESEARCH.md Part 2.
// publisherId is null in all regions until Awin publisher account is approved.
//
// Reconciled 2026-04-26 against docs/AFFILIATE-SETUP.md §3 (closes #217).
// Each row carries a `status` field consumed by buildAffiliateLink — only
// 'approved' rows produce live Awin URLs; other states return null in
// production (and the supplier baseUrl in dev-mode preview).
//
//   approved        — programme accepted, ready to drive traffic
//   applied         — application submitted, awaiting decision
//   pending-review  — not yet applied (e.g. not in Awin H&G export)
//   inactive        — explicitly not in scope (e.g. Crocus 222d payment / L4)
//
// Approved-but-niche-mismatch programmes (Doctor Blooms 26643, Gardenista
// 37292) are intentionally NOT listed here — now wired into AFFILIATE_PARTNERS
// — see Plan 02 (Phase 36 D-06).
//
// ── Phase 35 schema additions (D-01, D-04, D-09, D-10) ─────────────────────
//
//   priority?: number
//     Root-level. Higher = preferred. Tiebreaker among equally-specialty-
//     matched suppliers per D-09. Defaults to 0 when absent. Phase 35 seed
//     defaults follow D-10: David Austin 100, Sarah Raven 90, Crocus 80,
//     Thompson & Morgan 70, YouGarden 60, Gardening Direct 50, Harkness 40.
//     New Phase 36 suppliers default to 50 unless explicitly set.
//
//   regions[code].searchUrlTemplate?: string
//     Search-results URL on the merchant's domain with a literal `{QUERY}`
//     placeholder, substituted (URL-encoded) by `resolveSearchUrl`. Lives
//     INSIDE each `regions[code]` block per D-04 — different regions can
//     carry different search syntaxes. Suppliers without a template are
//     skipped by `findMatchingSuppliers` per D-03 (treated identically to
//     a status-blocked or URL-null supplier — never dump a user on a
//     homepage). The substitution must run BEFORE the Awin `cread.php`
//     wrapping in `buildAffiliateLink` to preserve single-encoding (see
//     Pitfall 2 in 35-RESEARCH.md).
//
//     Search templates verified during Phase 35 build (RESEARCH §"Approved
//     Suppliers — Search URL Sweep"); re-verification is automated by the
//     `/verify-supplier-search-templates` skill (Phase 35 Plan 04).
//
// ── Phase 36 schema addition (D-05) ────────────────────────────────────────
//
//   reason?: string
//     Root-level. Optional human-readable explanation of WHY a supplier is
//     not currently approved. Populated whenever `status !== 'approved'`;
//     never present on approved rows. Examples (paraphrased — see rows below
//     for the verbatim strings):
//       status: 'applied'   → "Application pending decision since YYYY-MM-DD"
//       status: 'inactive'  → "Not in Awin H&G directory — re-check periodically"
//       status: 'inactive'  → "Awin 222-day payment terms — see AFFILIATE-SETUP.md §3"
//     Mirrors the carry-forward source of truth in docs/AFFILIATE-SETUP.md §3.
//     Phase 36 D-05.
// ---------------------------------------------------------------------------

export const PLANT_SUPPLIERS = Object.freeze([
  {
    id: 'thompson-morgan',
    name: 'Thompson & Morgan',
    plantTypes: ['shrub', 'perennial', 'bulb', 'annual', 'herb', 'climber', 'fruit tree'],
    specialty: ['seeds', 'bulbs', 'bedding'],
    status: 'applied',
    priority: 70,
    // thompson-morgan GB searchUrlTemplate intentionally omitted — manual sweep in Phase 35 Plan 04 (Bloomreach search, no public URL pattern). D-03 skips suppliers with no template.
    regions: { GB: { network: 'awin', merchantId: '2283', baseUrl: 'https://www.thompson-morgan.com' } },
  },
  {
    id: 'sarah-raven',
    name: 'Sarah Raven',
    plantTypes: ['bulb', 'perennial', 'annual'],
    specialty: ['cutting garden', 'premium bulbs'],
    status: 'inactive',
    priority: 90,
    reason: 'Not in Awin H&G export 2026-04 — re-check periodically',
    regions: { GB: {
      network: 'awin',
      merchantId: '37152',
      baseUrl: 'https://www.sarahraven.com',
      searchUrlTemplate: 'https://www.sarahraven.com/search?q={QUERY}',
    } },
  },
  {
    id: 'suttons',
    name: 'Suttons',
    plantTypes: ['bulb', 'annual', 'perennial', 'herb'],
    specialty: ['seeds', 'bulbs'],
    status: 'applied',
    priority: 55,
    reason: 'Application pending decision since 2026-04-18',
    regions: { GB: {
      network: 'awin',
      merchantId: '25121',
      baseUrl: 'https://www.suttons.co.uk',
      searchUrlTemplate: 'https://www.suttons.co.uk/search?q={QUERY}',
    } },
  },
  {
    id: 'yougarden',
    name: 'YouGarden',
    plantTypes: ['shrub', 'perennial', 'bulb', 'fruit tree', 'climber'],
    specialty: ['budget', 'broad range'],
    status: 'approved',
    priority: 60,
    regions: { GB: {
      network: 'awin',
      merchantId: '5686',
      baseUrl: 'https://www.yougarden.com',
      searchUrlTemplate: 'https://www.yougarden.com/search/results?search={QUERY}',
    } },
  },
  {
    id: 'cherry-lane',
    name: 'Cherry Lane Garden Centres',
    plantTypes: ['shrub', 'perennial', 'bulb', 'annual'],
    specialty: ['broad range', 'garden centre'],
    status: 'applied',
    priority: 50,
    reason: 'Application pending decision since 2026-04-18',
    regions: { GB: {
      network: 'awin',
      merchantId: '15368',
      baseUrl: 'https://www.cherry-lane.co.uk',
      searchUrlTemplate: 'https://www.cherry-lane.co.uk/search?q={QUERY}',
    } },
  },
  {
    id: 'crocus',
    name: 'Crocus',
    plantTypes: ['shrub', 'perennial', 'tree', 'climber', 'evergreen shrub'],
    specialty: ['RHS partner', 'broad range'],
    status: 'inactive',
    priority: 80,
    regions: { GB: {
      network: 'awin',
      merchantId: '7833',
      baseUrl: 'https://www.crocus.co.uk',
      // searchUrlTemplate intentionally omitted while status: 'inactive' (Phase 35 IN-02).
      // Awin 222-day payment terms make this supplier non-viable for now —
      // see docs/AFFILIATE-SETUP.md §3. Re-add the template alongside
      // status: 'approved' when the supplier is reactivated.
    } },
  },
  {
    id: 'david-austin-roses',
    name: 'David Austin Roses',
    plantTypes: ['shrub'],
    specialty: ['roses'],
    status: 'inactive',
    priority: 100,
    reason: 'Not in Awin H&G export 2026-04 — re-check periodically',
    regions: { GB: {
      network: 'awin',
      merchantId: '53135',
      baseUrl: 'https://www.davidaustinroses.com',
      searchUrlTemplate: 'https://www.davidaustinroses.com/search?q={QUERY}',
    } },
  },
  {
    id: 'dobies',
    name: 'Dobies',
    plantTypes: ['bulb', 'annual', 'perennial', 'herb'],
    specialty: ['seeds', 'bulbs'],
    status: 'applied',
    priority: 55,
    reason: 'Application pending decision since 2026-04-18',
    regions: { GB: {
      network: 'awin',
      merchantId: '25131',
      baseUrl: 'https://www.dobies.co.uk',
      searchUrlTemplate: 'https://www.dobies.co.uk/search?q={QUERY}',
    } },
  },
  {
    id: 'flying-flowers',
    name: 'Flying Flowers',
    plantTypes: ['bulb', 'annual'],
    specialty: ['gifts', 'bouquets'],
    status: 'applied',
    priority: 30,
    reason: 'Application pending decision since 2026-04-18',
    // flying-flowers GB searchUrlTemplate intentionally omitted — JavaScript-only search, no public URL pattern (mirror thompson-morgan posture). D-03 skips suppliers with no template.
    regions: { GB: { network: 'awin', merchantId: '1268', baseUrl: 'https://www.flyingflowers.co.uk' } },
  },
  {
    id: 'gardening-direct',
    name: 'Gardening Direct',
    plantTypes: ['shrub', 'perennial', 'bulb', 'annual', 'climber'],
    specialty: ['plug plants', 'broad range'],
    status: 'approved',
    priority: 50,
    regions: { GB: {
      network: 'awin',
      merchantId: '1351',
      baseUrl: 'https://www.gardeningdirect.co.uk',
      searchUrlTemplate: 'https://www.gardeningdirect.co.uk/search.htm?Ntt={QUERY}',
    } },
  },
  {
    id: 'harkness-roses',
    name: 'Harkness Roses',
    plantTypes: ['shrub'],
    specialty: ['roses'],
    status: 'approved',
    priority: 40,
    regions: { GB: {
      network: 'awin',
      merchantId: '32111',
      baseUrl: 'https://www.roses.co.uk',
      searchUrlTemplate: 'https://www.roses.co.uk/search?q={QUERY}',
    } },
  },
]);

// ---------------------------------------------------------------------------
// 4b. AFFILIATE_PARTNERS (Phase 36 D-06) — dormant non-plant approved merchants
//
// Approved Awin merchants whose product mix doesn't fit PLANT_SUPPLIERS.
// Same shape as PLANT_SUPPLIERS plus a `category: string` field. NO UI consumer
// is wired in Phase 36 — this export ships dormant for a future MO-19 phase
// to drive Home Panel cards keyed by category.
//
// findMatchingSuppliers, pickInspire, FeaturedSupplierCard, and the supplier-
// coverage tiebreaker DELIBERATELY DO NOT iterate this export (Phase 36 D-07).
// They remain plant-only.
//
// Categories are slugified short-list values:
//   'indoor-plants'    — hydroponics, grow lights, plant nutrients
//   'garden-furniture' — outdoor cushions, soft furnishings
//
// Re-uses the same status-gated buildAffiliateLink path as PLANT_SUPPLIERS
// (the Awin path already accepts any product with regions[code].network ===
// 'awin'). When MO-19 wires consumers, they call buildAffiliateLink directly
// — no new URL resolver needed.
//
// Status field semantics mirror PLANT_SUPPLIERS:
//   'approved'       — programme accepted, ready to drive traffic
//   'applied'        — application submitted, awaiting decision
//   'pending-review' — not yet applied
//   'inactive'       — explicitly not in scope
//
// Per AFFILIATE-SETUP.md §3 (verified 2026-04-19 / 2026-04-21), both seed
// entries below are `status: 'approved'`. They produce live Awin URLs if
// buildAffiliateLink is ever called against them — there is no consumer in
// Phase 36, so no traffic flows.
// ---------------------------------------------------------------------------

export const AFFILIATE_PARTNERS = Object.freeze([
  {
    id: 'doctor-blooms',
    name: 'Doctor Blooms',
    category: 'indoor-plants',
    specialty: ['hydroponics', 'grow lights', 'plant nutrients'],
    status: 'approved',
    priority: 50,
    regions: { GB: {
      network: 'awin',
      merchantId: '26643',
      baseUrl: 'https://www.doctorblooms.co.uk',
    } },
  },
  {
    id: 'gardenista',
    name: 'Gardenista',
    category: 'garden-furniture',
    specialty: ['outdoor cushions', 'soft furnishings'],
    status: 'approved',
    priority: 50,
    regions: { GB: {
      network: 'awin',
      merchantId: '37292',
      // Domain verified via WebSearch + Trustpilot — gardenistauk.com is the
      // UK Awin merchant 37292 storefront. Reject any future edit that swaps
      // it for the .co.uk variant or the bare .com (a US shopping content
      // brand). Operator cross-checks the Awin merchant 37292 dashboard if
      // any doubt remains. RESEARCH.md §A8.
      baseUrl: 'https://gardenistauk.com',
    } },
  },
]);

// ---------------------------------------------------------------------------
// 5. AFFILIATE_PRODUCTS (D-02) — 12 curated GB products
//
// taskTypes mirror PRODUCT_RULES *categories* ('feed', 'mulch', 'pest', 'other'),
// NOT the task type strings used in plant.tasks ('prune', 'feed', etc.).
// This distinction matters: PRODUCT_RULES.category is the relevant classifier
// for matching a task's product category to a product suggestion.
//
// links.GB contains '{tag}' as a placeholder; buildAffiliateLink substitutes
// the resolved region's amazon.tag at runtime (D-12).
//
// ASINs are real, researcher-verified Amazon UK ASINs (see 260413-kt4 research).
// Products whose asin still starts with 'TODO-' are FILTERED OUT at export time
// (see the .filter() applied to this array below) — their Amazon URLs would 404
// and breach Associates T&Cs. Records stay in source so re-enabling is a single-
// word edit (replace the TODO- prefix with a real ASIN).
// ---------------------------------------------------------------------------

export const AFFILIATE_PRODUCTS = Object.freeze([
  {
    id: 'tomorite-1l',
    name: 'Tomorite Liquid Tomato Feed 1L',
    asin: 'B09RK3HPH5',
    taskTypes: ['feed'],
    plantTypes: ['fruit tree', 'shrub'],
    keywords: ['high-potash', 'tomato feed', 'hydrangea feed'],
    productRuleKey: 'highpotash',
    links: { GB: 'https://www.amazon.co.uk/dp/B09RK3HPH5?tag={tag}' },
    priceIndicator: '~£5',
  },
  {
    id: 'growmore-1-5kg',
    name: 'Westland Growmore Garden Fertiliser 1.5kg',
    asin: 'B001P3YXFE',
    taskTypes: ['feed'],
    plantTypes: [],
    keywords: ['balanced', 'general', 'growmore'],
    productRuleKey: 'granular',
    links: { GB: 'https://www.amazon.co.uk/dp/B001P3YXFE?tag={tag}' },
    priceIndicator: '~£6',
  },
  {
    id: 'miracle-gro-ericaceous',
    name: 'Miracle-Gro Azalea, Camellia & Rhododendron Continuous Release Plant Food 1kg',
    asin: 'B001880UGM',
    taskTypes: ['feed'],
    plantTypes: ['shrub', 'evergreen shrub'],
    keywords: ['ericaceous', 'acid-loving'],
    productRuleKey: 'ericaceous',
    links: { GB: 'https://www.amazon.co.uk/dp/B001880UGM?tag={tag}' },
    priceIndicator: '~£8',
  },
  {
    id: 'bone-meal-10kg',
    name: 'Vitax Bone Meal 10kg',
    asin: 'B0041WDAPU',
    taskTypes: ['feed'],
    plantTypes: ['bulb', 'tree'],
    keywords: ['bone meal', 'bonemeal', 'bulb'],
    productRuleKey: 'bonemeal',
    links: { GB: 'https://www.amazon.co.uk/dp/B0041WDAPU?tag={tag}' },
    priceIndicator: '~£20',
  },
  {
    id: 'sulphate-of-potash',
    name: 'Vitax Sulphate of Potash 1.25kg',
    asin: 'B002SP9ZRY',
    taskTypes: ['feed'],
    plantTypes: ['fruit tree'],
    keywords: ['sulphate of potash', 'fruit feed'],
    productRuleKey: 'fruittreefeed',
    links: { GB: 'https://www.amazon.co.uk/dp/B002SP9ZRY?tag={tag}' },
    priceIndicator: '~£10',
  },
  {
    id: 'chicken-manure-10kg',
    name: 'Westland Organic Chicken Manure Pellets 10kg',
    asin: 'B0771Q4N7Z',
    taskTypes: ['feed'],
    plantTypes: ['perennial', 'shrub'],
    keywords: ['chicken manure', 'manure pellet'],
    productRuleKey: 'chickenmanurepellets',
    links: { GB: 'https://www.amazon.co.uk/dp/B0771Q4N7Z?tag={tag}' },
    priceIndicator: '~£16',
  },
  {
    id: 'westland-bark-chippings',
    name: 'Westland Landscaping Bark 100L',
    asin: 'B0055SQEJS',
    taskTypes: ['mulch'],
    plantTypes: [],
    keywords: ['bark', 'bark mulch', 'composted bark'],
    productRuleKey: 'bark',
    links: { GB: 'https://www.amazon.co.uk/dp/B0055SQEJS?tag={tag}' },
    priceIndicator: '~£18',
  },
  {
    id: 'leaf-mould-50l',
    name: 'Melcourt Composted Fine Bark 50L',
    asin: 'B00WUQCPXM',
    taskTypes: ['mulch'],
    plantTypes: ['fern', 'perennial'],
    keywords: ['leaf mould'],
    productRuleKey: 'leafmould',
    links: { GB: 'https://www.amazon.co.uk/dp/B00WUQCPXM?tag={tag}' },
    priceIndicator: '~£14',
  },
  {
    id: 'nemaslug',
    name: 'Nemaslug Slug Killer Nematodes',
    asin: 'B004UFTDKO',
    taskTypes: ['other'],
    plantTypes: ['perennial'],
    keywords: ['slug', 'nematode'],
    productRuleKey: 'slugpellets',
    links: { GB: 'https://www.amazon.co.uk/dp/B004UFTDKO?tag={tag}' },
    priceIndicator: '~£15',
  },
  {
    id: 'ferric-slug-pellets',
    name: 'Growing Success Slug Killer (Ferric Phosphate) 575g',
    asin: 'B01MS8RI7R',
    taskTypes: ['other'],
    plantTypes: ['perennial'],
    keywords: ['slug'],
    productRuleKey: 'slugpellets',
    links: { GB: 'https://www.amazon.co.uk/dp/B01MS8RI7R?tag={tag}' },
    priceIndicator: '~£8',
  },
  {
    id: 'horticultural-fleece',
    name: 'Abimars Horticultural Garden Fleece 30gsm 2m x 10m',
    asin: 'B09NKGYSNS',
    taskTypes: ['other'],
    plantTypes: ['tree', 'fruit tree'],
    keywords: ['fleece'],
    productRuleKey: 'fleece',
    links: { GB: 'https://www.amazon.co.uk/dp/B09NKGYSNS?tag={tag}' },
    priceIndicator: '~£9',
  },
  {
    id: 'bamboo-canes-pack',
    name: 'Bamboo Canes 120cm Pack of 20',
    asin: 'B0B6RNF1FB',
    taskTypes: ['other'],
    plantTypes: ['perennial'],
    keywords: ['stake', 'cane', 'support'],
    productRuleKey: 'stakes',
    links: { GB: 'https://www.amazon.co.uk/dp/B0B6RNF1FB?tag={tag}' },
    priceIndicator: '~£8',
  },
  {
    id: 'sb-plant-invigorator',
    name: 'SB Plant Invigorator Bug Killer 500ml',
    asin: 'B00DP2BKFE',
    taskTypes: ['other'],
    plantTypes: [],
    keywords: ['aphid', 'whitefly', 'mildew', 'red spider mite', 'mealybug', 'organic pest'],
    productRuleKey: 'aphidcontrol',
    links: { GB: 'https://www.amazon.co.uk/dp/B00DP2BKFE?tag={tag}' },
    priceIndicator: '~£10',
  },
  {
    id: 'vine-weevil-killer',
    name: 'BugClear Ultra Vine Weevil Killer 480ml',
    asin: 'B00KD8NB2S',
    taskTypes: ['other'],
    plantTypes: ['perennial'],
    keywords: ['vine weevil', 'weevil', 'heuchera', 'strawberry pest', 'container pest'],
    productRuleKey: 'vineweevil',
    links: { GB: 'https://www.amazon.co.uk/dp/B00KD8NB2S?tag={tag}' },
    priceIndicator: '~£12',
  },
  {
    id: 'rose-shield-bug-fungus',
    name: 'RoseClear Ultra Gun! 1L',
    asin: 'B071W5BQ16',
    taskTypes: ['other'],
    plantTypes: ['shrub', 'climber'],
    keywords: ['rose', 'blackspot', 'rust', 'aphid', 'rose pest', 'rose disease'],
    productRuleKey: 'roseprotect',
    links: { GB: 'https://www.amazon.co.uk/dp/B071W5BQ16?tag={tag}' },
    priceIndicator: '~£12',
  },
  {
    // Phase 36 D-11 (corrected per RESEARCH §A9): productRuleKey is 'roseprotect',
    // NOT 'fungalspray'. Shares the rule with rose-shield-bug-fungus above.
    // Distinct SKU from the RTU 1L gun (B071W5BQ16) — this is the 200ml concentrate (B004DI77TC).
    id: 'roseclear-ultra-concentrate',
    name: 'RoseClear Ultra Concentrate 200ml',
    asin: 'B004DI77TC',
    taskTypes: ['pest'],
    plantTypes: ['shrub'],
    keywords: ['rose', 'aphid', 'blackfly', 'greenfly', 'rose pest', 'rose blackspot', 'roseclear'],
    productRuleKey: 'roseprotect',
    links: { GB: 'https://www.amazon.co.uk/dp/B004DI77TC?tag={tag}' },
    priceIndicator: '~£12',
  },
  {
    id: 'fruit-tree-grease-band',
    name: 'Vitax Greasebands (Fruit Tree Barrier)',
    asin: 'B01AG2OCBY',
    taskTypes: ['other'],
    plantTypes: ['fruit tree'],
    keywords: ['winter moth', 'codling moth', 'grease band', 'greaseband', 'caterpillar'],
    productRuleKey: 'greaseband',
    links: { GB: 'https://www.amazon.co.uk/dp/B01AG2OCBY?tag={tag}' },
    priceIndicator: '~£8',
  },
  {
    id: 'copper-slug-tape',
    name: 'Vitax 4m Copper Slug Tape',
    asin: 'B0041WIRVM',
    taskTypes: ['other'],
    plantTypes: ['perennial'],
    keywords: ['slug', 'snail', 'copper', 'barrier', 'pot rim'],
    productRuleKey: 'slugpellets',
    links: { GB: 'https://www.amazon.co.uk/dp/B0041WIRVM?tag={tag}' },
    priceIndicator: '~£7',
  },
  // --- SUP-01 pest products (real Amazon UK ASINs since Phase 36 D-10) ---
  {
    id: 'box-tree-pheromone-trap',
    name: 'Agralan Box Tree Caterpillar Pheromone Trap',
    asin: 'B09TWBMRPK',                                                    // was Phase 26 placeholder (swapped to real ASIN — Phase 36 D-10)
    taskTypes: ['pest'],
    plantTypes: ['shrub', 'topiary'],
    keywords: ['box tree moth', 'box moth', 'buxus', 'pheromone', 'boxtreemoth'],
    productRuleKey: 'boxtreemoth',
    links: { GB: 'https://www.amazon.co.uk/dp/B09TWBMRPK?tag={tag}' },     // dual-edit with asin field above
    priceIndicator: '~£15',
  },
  {
    id: 'xentari-bt-spray',
    name: 'XenTari Bacillus thuringiensis Caterpillar Spray',
    asin: 'B09ZHRRDLC',                                                    // was Phase 26 placeholder (swapped to real ASIN — Phase 36 D-10)
    taskTypes: ['pest'],
    plantTypes: ['shrub', 'topiary', 'herb'],
    keywords: ['xentari', 'bacillus thuringiensis', 'bt spray', 'caterpillar', 'box tree moth', 'boxtreemoth', 'caterpillarcontrol'],
    productRuleKey: 'caterpillarcontrol',
    links: { GB: 'https://www.amazon.co.uk/dp/B09ZHRRDLC?tag={tag}' },     // dual-edit with asin field above
    priceIndicator: '~£18',
  },
  {
    id: 'nemasys-vine-weevil',
    name: 'Nemasys Vine Weevil Killer Nematodes',
    asin: 'B06XS1BVWW',                                                    // was Phase 26 placeholder (swapped to real ASIN — Phase 36 D-10)
    taskTypes: ['pest'],
    plantTypes: ['perennial', 'shrub'],
    keywords: ['vine weevil', 'vineweevil', 'nemasys', 'nematode', 'heuchera', 'fuchsia', 'hydrangea'],
    productRuleKey: 'vineweevil',
    links: { GB: 'https://www.amazon.co.uk/dp/B06XS1BVWW?tag={tag}' },     // dual-edit with asin field above
    priceIndicator: '~£12',
  },
  {
    id: 'codling-moth-trap',
    name: 'Agralan Codling Moth Pheromone Trap',
    asin: 'B0039O632S',                                                    // was Phase 26 placeholder (swapped to real ASIN — Phase 36 D-10)
    taskTypes: ['pest'],
    plantTypes: ['fruit tree'],
    keywords: ['codling moth', 'codling', 'fruitmothtrap', 'apple', 'pear'],
    productRuleKey: 'fruitmothtrap',
    links: { GB: 'https://www.amazon.co.uk/dp/B0039O632S?tag={tag}' },     // dual-edit with asin field above
    priceIndicator: '~£12',
  },
  {
    id: 'provanto-fungus-fighter',
    name: 'Provanto Fungus Fighter Concentrate',
    asin: 'B07MSQJPDJ',                                                    // was Phase 26 placeholder (swapped to real ASIN — Phase 36 D-10)
    taskTypes: ['pest'],
    plantTypes: ['shrub', 'perennial'],
    keywords: ['fungicide', 'fungal', 'fungalspray', 'blackspot', 'rust', 'powdery mildew', 'grey mould', 'botrytis'],
    productRuleKey: 'fungalspray',
    links: { GB: 'https://www.amazon.co.uk/dp/B07MSQJPDJ?tag={tag}' },     // dual-edit with asin field above
    priceIndicator: '~£10',
  },
  {
    id: 'yellow-sticky-traps',
    name: 'Yellow Sticky Traps (pack)',
    asin: 'B0821Q9516',                                                    // was Phase 26 placeholder (swapped to real ASIN — Phase 36 D-10)
    taskTypes: ['pest'],
    plantTypes: ['perennial', 'annual', 'herb'],
    keywords: ['whitefly', 'whiteflycontrol', 'sticky trap', 'greenhouse'],
    productRuleKey: 'whiteflycontrol',
    links: { GB: 'https://www.amazon.co.uk/dp/B0821Q9516?tag={tag}' },     // dual-edit with asin field above
    priceIndicator: '~£8',
  },
  // --- Phase 27 ENG-02 — Tool products (ASINs are placeholders; user swaps before deployment) ---
  {
    id: 'bypass-secateurs',
    name: 'Bypass Secateurs',
    asin: 'TODO-bypass-secateurs',
    taskTypes: ['tool'],
    plantTypes: ['shrub', 'perennial', 'climber'],
    keywords: ['secateurs', 'pruning', 'cut back'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-bypass-secateurs?tag={tag}' },
    priceIndicator: '~£15',
  },
  {
    id: 'garden-gloves',
    name: 'Leather Garden Gloves',
    asin: 'TODO-garden-gloves',
    taskTypes: ['tool'],
    plantTypes: [],
    keywords: ['gloves', 'gardening gloves', 'hand protection'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-garden-gloves?tag={tag}' },
    priceIndicator: '~£10',
  },
  {
    id: 'garden-twine',
    name: 'Natural Jute Garden Twine',
    asin: 'TODO-garden-twine',
    taskTypes: ['tool'],
    plantTypes: ['climber', 'perennial'],
    keywords: ['twine', 'tie in', 'string'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-garden-twine?tag={tag}' },
    priceIndicator: '~£5',
  },
  {
    id: 'bamboo-labels',
    name: 'Wooden Plant Labels (pack)',
    asin: 'TODO-bamboo-labels',
    taskTypes: ['tool'],
    plantTypes: [],
    keywords: ['labels', 'plant labels', 'plant markers'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-bamboo-labels?tag={tag}' },
    priceIndicator: '~£6',
  },
  {
    id: 'watering-can-9l',
    name: '9L Watering Can',
    asin: 'TODO-watering-can-9l',
    taskTypes: ['tool'],
    plantTypes: [],
    keywords: ['watering can', 'watering'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-watering-can-9l?tag={tag}' },
    priceIndicator: '~£18',
  },
  {
    id: 'hand-trowel',
    name: 'Stainless Steel Hand Trowel',
    asin: 'TODO-hand-trowel',
    taskTypes: ['tool'],
    plantTypes: ['bulb', 'annual', 'perennial'],
    keywords: ['trowel', 'planting', 'plant/sow'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-hand-trowel?tag={tag}' },
    priceIndicator: '~£12',
  },
  {
    id: 'potting-compost',
    name: 'Peat-Free Potting Compost 50L',
    asin: 'TODO-potting-compost',
    taskTypes: ['tool'],
    plantTypes: ['annual', 'herb', 'perennial'],
    keywords: ['potting compost', 'compost', 'pot on', 'repot', 'grow bag'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-potting-compost?tag={tag}' },
    priceIndicator: '~£10',
  },
  {
    id: 'garden-shears',
    name: 'Garden Shears',
    asin: 'TODO-garden-shears',
    taskTypes: ['tool'],
    plantTypes: ['shrub', 'topiary', 'evergreen shrub'],
    keywords: ['shears', 'hedge', 'topiary', 'clip', 'shape'],
    productRuleKey: null,
    links: { GB: 'https://www.amazon.co.uk/dp/TODO-garden-shears?tag={tag}' },
    priceIndicator: '~£25',
  },
].filter(p => !String(p.asin || '').startsWith('TODO-')));

// ---------------------------------------------------------------------------
// 6. AFFILIATE_DISCLOSURE (per docs/AFFILIATE-RESEARCH.md §Part 11)
//
// UK English. Both strings must be exact so Phase 19 can render them verbatim.
// ---------------------------------------------------------------------------

export const AFFILIATE_DISCLOSURE = Object.freeze({
  amazon:  'As an Amazon Associate I earn from qualifying purchases.',
  general: 'We may earn a small commission from these links — it helps keep Tend core features free.',
});

// ---------------------------------------------------------------------------
// 7. detectRegion() — region detection with 'auto' sentinel (D-03, D-04, D-06)
//
// Reads tend:region from localStorage.
//   - If 'auto' → re-derive from navigator.language every call (sentinel persists).
//   - If null / '' → derive, persist, and return.
//   - If a known region code → return it directly.
//   - If unknown stored value → fall back to DEFAULT_REGION.
//
// Also exports resolveLangToRegion() and __FALLBACK_TABLE for direct test access.
// ---------------------------------------------------------------------------

/**
 * Resolve a BCP-47 language tag (e.g. 'en-GB', 'de-CH') to a seeded region code.
 * Exported for unit testing without touching localStorage.
 * @param {string} langCode - e.g. navigator.language value
 * @returns {'GB'|'DE'|'FR'|'NL'} region code, never null
 */
export function resolveLangToRegion(langCode) {
  const lang = langCode || 'en-GB';
  const langRegion = (lang.split('-')[1] || 'GB').toUpperCase();
  const resolved = FALLBACK_TABLE[langRegion];
  // If fallback exists and is a seeded region, use it; otherwise DEFAULT_REGION
  if (resolved && AFFILIATE_REGIONS[resolved]) return resolved;
  return DEFAULT_REGION;
}

/**
 * Detect and persist the current user region.
 * Returns one of the seeded region codes: 'GB' | 'DE' | 'FR' | 'NL'.
 * Never returns null.
 */
export function detectRegion() {
  let stored = null;
  try { stored = localStorage.getItem('tend:region'); } catch (_) { stored = null; }

  // 'auto' sentinel — re-derive on each call without overwriting the sentinel
  if (stored === 'auto') {
    const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';
    return resolveLangToRegion(lang);
  }

  // First visit (null or empty) — derive, persist, and return
  if (stored === null || stored === '') {
    const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';
    const resolved = resolveLangToRegion(lang);
    try { localStorage.setItem('tend:region', resolved); } catch (_) {}
    return resolved;
  }

  // Known seeded region stored — return directly
  if (AFFILIATE_REGIONS[stored]) return stored;

  // Unknown stored value — fall back to default (M2: tamper-resilient)
  return DEFAULT_REGION;
}

// ---------------------------------------------------------------------------
// 8. buildAffiliateLink() — single URL builder (D-07, D-09, D-12)
// ---------------------------------------------------------------------------

/**
 * Build an affiliate URL with 5-segment SubID encoding.
 *
 * Returns null when the resolved region has no Amazon tag AND no Awin
 * publisher ID configured (the current state until accounts are approved).
 * CALLERS MUST NOT invoke logAffiliateClick when this returns null.
 *
 * Supported URL shapes:
 *   Amazon — https://www.amazon.co.uk/dp/{asin}?tag={tag}&ascsubtag={subId}
 *   Awin   — https://www.awin1.com/cread.php?awinmid=...&awinaffid=...&clickref=...&url=...
 *
 * Phase 19 must render links with rel="noopener noreferrer" target="_blank".
 * (T-18-11 — enforced in Phase 19's threat model.)
 *
 * Phase 35 (D-01): the Awin branch now reads `context.plantName` and, when
 * the supplier has a `regions[region].searchUrlTemplate`, substitutes
 * `{QUERY}` with the URL-encoded plant name and wraps the resolved
 * search-results URL in cread.php's `&url=` parameter. Callers without
 * `plantName` (e.g. SupplierSheet) fall back to `baseUrl` — zero
 * behavioural change for that path.
 *
 * @param {object} product - entry from AFFILIATE_PRODUCTS or PLANT_SUPPLIERS
 * @param {object} context - { feature, plantType, task, month, device?, region?, plantName? }
 * @returns {string|null} URL or null when no tag/publisherId configured
 */
// Dev-mode preview override: when Developer Mode is active and live tags are
// not yet configured (Amazon Associates / Awin approval pending), buildAffiliateLink
// falls back to the supplier's direct baseUrl so the SupplierSheet UI can be
// visually verified on staging. NEVER hits the affiliate networks with bogus IDs.
function isDevPreview() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('tend:devmode') === 'true';
  } catch { return false; }
}

/**
 * Resolve a supplier's search URL template by substituting {QUERY} with the
 * URL-encoded plant common name. Returns null when the supplier has no
 * searchUrlTemplate for the active region (D-03).
 *
 * Substitution must run BEFORE wrapping in cread.php — see Pitfall 2 in
 * 35-RESEARCH.md (URLSearchParams encodes the value once, so {QUERY} must
 * already be substituted at that point).
 *
 * @param {object} supplier - PLANT_SUPPLIERS entry
 * @param {string} plantName - common name (e.g. "English Lavender")
 * @param {string} region - region code e.g. 'GB'
 * @returns {string|null} resolved deep URL, or null
 */
export function resolveSearchUrl(supplier, plantName, region) {
  const tmpl = supplier?.regions?.[region]?.searchUrlTemplate;
  if (!tmpl || !plantName) return null;
  // Misconfigured template — fail closed (Phase 35 IN-01).
  // A template missing {QUERY} would otherwise return a hard-coded URL that
  // ignores the plant name, silently sending every search to the same page.
  if (!tmpl.includes('{QUERY}')) return null;
  return tmpl.replace('{QUERY}', encodeURIComponent(plantName));
}

export function buildAffiliateLink(product, context = {}) {
  const region = context.region || detectRegion();
  const regionCfg = AFFILIATE_REGIONS[region];
  if (!regionCfg) return null;

  const subId = buildSubId({
    feature:   context.feature,
    plantType: context.plantType,
    task:      context.task,
    month:     context.month,
    device:    context.device,
  });

  // Amazon path — product has asin and links[region]
  if (product && product.asin && product.links && product.links[region]) {
    const tag = regionCfg.amazon && regionCfg.amazon.tag;
    if (!tag) {
      if (isDevPreview()) {
        // Strip {tag} param from preview URL so we don't send bogus traffic
        return product.links[region].replace(/[?&]tag=\{tag\}/, '').replace(/\{tag\}/, '');
      }
      return null;
    }
    // Substitute {tag} placeholder and append SubID as ascsubtag (M7)
    let url = product.links[region].replace('{tag}', tag);
    const sep = url.includes('?') ? '&' : '?';
    url += `${sep}ascsubtag=${encodeURIComponent(subId)}`;
    return url;
  }

  // Awin path — product is a PLANT_SUPPLIERS entry with regions[region].network === 'awin'
  if (
    product &&
    product.regions &&
    product.regions[region] &&
    product.regions[region].network === 'awin'
  ) {
    const pubId = regionCfg.awin && regionCfg.awin.publisherId;
    const merchantId = product.regions[region].merchantId;
    const baseUrl    = product.regions[region].baseUrl;
    // Phase 35 (D-01): resolve the merchant URL ONCE before any branching.
    // When the supplier has a regions[region].searchUrlTemplate AND the
    // caller provided context.plantName, this is the templated search-
    // results URL with {QUERY} substituted (single-encoded). Otherwise it
    // falls back to baseUrl — preserving zero behavioural change for
    // callers that don't pass plantName (SupplierSheet, etc.).
    // Substitution happens HERE, before URLSearchParams wraps the value
    // into cread.php's &url= param, so encoding stays single-pass
    // (Pitfall 2 in 35-RESEARCH.md).
    const resolved = resolveSearchUrl(product, context.plantName, region) || baseUrl;
    // Status gate (#217): only 'approved' suppliers produce live URLs.
    // Rows with 'applied' / 'pending-review' / 'inactive' return null in
    // production; dev preview still surfaces the resolved URL directly so
    // dev-mode QA can exercise the new search-template behaviour
    // (Pitfall 4 in 35-RESEARCH.md).
    if (product.status && product.status !== 'approved') {
      if (isDevPreview()) return resolved;
      return null;
    }
    if (!pubId) {
      // Dev-mode preview: open the resolved URL directly, bypass Awin
      if (isDevPreview()) return resolved;
      return null;
    }
    const params = new URLSearchParams({
      awinmid:  merchantId,
      awinaffid: pubId,
      clickref: subId,
      url:      resolved,
    });
    return `https://www.awin1.com/cread.php?${params.toString()}`;
  }

  return null;
}

// ---------------------------------------------------------------------------
// 9. logAffiliateClick() — privacy-light click logger (D-10, D-11, D-13)
// ---------------------------------------------------------------------------

/**
 * Append a click entry to tend:affiliateClicks (FIFO cap 500).
 *
 * IDs only — never plant name or latin name — keeps privacy footprint small
 * and avoids duplicating mutable display data in the log.
 *
 * Never throws. Never blocks navigation. Silent in prod; console.warn in dev.
 * Wrapped in try/catch to handle localStorage QuotaExceeded and private mode.
 *
 * @param {object} entry - { linkId, feature, plantId, prospectId, plantType, areaId, supplier, month, device }
 * @returns {undefined}
 */
export function logAffiliateClick(entry) {
  try {
    const raw = localStorage.getItem('tend:affiliateClicks');
    const arr  = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(arr) ? arr : [];
    list.push({
      ts:         Date.now(),
      linkId:     entry.linkId     ?? null,
      feature:    entry.feature    ?? null,
      plantId:    entry.plantId    ?? null,
      // prospectId joins pre-add Ask Tend supplier clicks to the resulting
      // plant once persisted (plant.prospectId === click.prospectId). Enables
      // funnel: Ask Tend recommendation → supplier click → plant added → sale.
      prospectId: entry.prospectId ?? null,
      plantType:  entry.plantType  ?? null,
      areaId:     entry.areaId     ?? null,
      supplier:   entry.supplier   ?? null,
      month:      entry.month      ?? null,
      device:     entry.device     ?? detectDevice(),
    });
    // FIFO cap: enforce on write so storage cost is bounded regardless of
    // tampering (M3, M6). slice(-500) retains newest 500 entries.
    const capped = list.slice(-500);
    localStorage.setItem('tend:affiliateClicks', JSON.stringify(capped));
  } catch (err) {
    // eslint-disable-next-line no-undef
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[tend] affiliate click log write failed:', err);
    }
    // Silent in prod — navigation proceeds regardless.
  }
}

// ---------------------------------------------------------------------------
// 10. findMatchingProducts() — match AFFILIATE_PRODUCTS to a PRODUCT_RULES key (D-04)
// ---------------------------------------------------------------------------

/**
 * Find AFFILIATE_PRODUCTS entries that match a given PRODUCT_RULES key.
 * Matching chain: category match (PRODUCT_RULES.category === product.taskTypes)
 * then keyword refinement (at least one keyword overlaps).
 *
 * @param {string} productKey - a PRODUCT_RULES .key value (e.g. 'highpotash', 'bark')
 * @returns {Array} matching AFFILIATE_PRODUCTS entries, or []
 */
export function findMatchingProducts(productKey) {
  const rule = PRODUCT_RULES.find(r => r.key === productKey);
  if (!rule) return [];
  return AFFILIATE_PRODUCTS.filter(p => {
    // Step 1: category match — AFFILIATE_PRODUCTS.taskTypes matches PRODUCT_RULES.category
    if (!p.taskTypes.includes(rule.category)) return false;
    // Step 2: keyword refinement — at least one keyword overlaps
    if (p.keywords.length === 0) return true;
    return p.keywords.some(kw => rule.keywords.some(rk => rk.includes(kw) || kw.includes(rk)));
  });
}

// ---------------------------------------------------------------------------
// 11. buildGenericSearchUrl() — Amazon search fallback for rows with no specific match (D-05)
// ---------------------------------------------------------------------------

/**
 * Build a generic Amazon search URL for a PRODUCT_RULES key.
 * Returns null when the region has no Amazon tag or the key is unknown.
 * The tag is NOT encoded (per WR-03 fix pattern) — only the search query is encoded.
 *
 * @param {string} productKey - a PRODUCT_RULES .key value
 * @param {string} resolvedRegion - region code e.g. 'GB'
 * @returns {string|null} Amazon search URL or null
 */
export function buildGenericSearchUrl(productKey, resolvedRegion) {
  const rule = PRODUCT_RULES.find(r => r.key === productKey);
  if (!rule) return null;
  const regionCfg = AFFILIATE_REGIONS[resolvedRegion];
  if (!regionCfg?.amazon?.tag) return null;
  const query = encodeURIComponent(rule.label + ' gardening');
  return `https://www.${regionCfg.amazon.domain}/s?k=${query}&tag=${regionCfg.amazon.tag}`;
}

// ---------------------------------------------------------------------------
// 11a. buildManualSearchUrl() — Amazon search URL for user-typed manual products (SUP-02)
// ---------------------------------------------------------------------------

/**
 * Build an Amazon search URL for a manually-added product (SUP-02 Phase 26-03).
 * Unlike buildGenericSearchUrl which requires a PRODUCT_RULES key, this accepts
 * an arbitrary user-typed label. Used by the manual-row Buy button in
 * ShedInventory so manual products feel first-class alongside auto-detected
 * rows. Returns null when label is empty/whitespace OR the region has no
 * Amazon tag (same null-return discipline as buildGenericSearchUrl).
 *
 * Appends a clickref-style SubID built by the existing buildSubId helper so
 * attribution matches every other affiliate surface in the app (feature-
 * planttype-task-month-device). Encoded via encodeURIComponent — no user text
 * flows unescaped into the URL.
 *
 * @param {string} label - the user-typed product name (e.g. "Tomato feed")
 * @param {string} resolvedRegion - region code e.g. 'GB'
 * @returns {string|null} Amazon search URL or null
 */
export function buildManualSearchUrl(label, resolvedRegion) {
  const trimmed = (label || '').trim();
  if (!trimmed) return null;
  const regionCfg = AFFILIATE_REGIONS[resolvedRegion];
  if (!regionCfg?.amazon?.tag) return null;
  const query = encodeURIComponent(trimmed + ' gardening');
  const clickref = buildSubId({
    feature: FEATURE_SOURCES.SUPPLIES,
    plantType: 'na',
    task: 'pest',
    month: new Date().getMonth() + 1,
  });
  // clickref is a first-party tracking param; Amazon search accepts arbitrary
  // query-string keys. encodeURIComponent on the SubID for defence-in-depth
  // (the builder already slugifies each segment to [a-z0-9], but encoding
  // keeps the URL shape invariant if buildSubId ever widens its alphabet).
  return `https://www.${regionCfg.amazon.domain}/s?k=${query}&tag=${regionCfg.amazon.tag}&clickref=${encodeURIComponent(clickref)}`;
}

// ---------------------------------------------------------------------------
// 12. rowHasAffiliateLinks() — gate check for Buy button visibility (D-07, D-08)
// ---------------------------------------------------------------------------

/**
 * Check whether a product row has any buildable affiliate links.
 * Returns true if at least one specific AFFILIATE_PRODUCTS entry can build a URL,
 * or if a generic Amazon search link can be built (region has a tag).
 *
 * SUP-02 Phase 26-03: manual_\d+ keys return true when the region has an
 * Amazon tag — manual rows route through the same AffiliateSheet as
 * auto-detected rows, powered by buildManualSearchUrl.
 *
 * @param {string} productKey - a PRODUCT_RULES .key value OR manual_\d+
 * @param {string} resolvedRegion - region code e.g. 'GB'
 * @returns {boolean}
 */
export function rowHasAffiliateLinks(productKey, resolvedRegion) {
  // Manual products — Buy-able via Amazon search when region has a tag (SUP-02)
  if (typeof productKey === 'string' && /^manual_\d+$/.test(productKey)) {
    const regionCfg = AFFILIATE_REGIONS[resolvedRegion];
    return !!(regionCfg?.amazon?.tag);
  }
  // Existing behaviour — unchanged from 26-02
  const matches = findMatchingProducts(productKey);
  if (matches.length > 0) {
    const hasSpecific = matches.some(p => buildAffiliateLink(p, {
      feature: FEATURE_SOURCES.SUPPLIES,
      region: resolvedRegion,
      task: 'na',
      month: new Date().getMonth() + 1,
    }) !== null);
    if (hasSpecific) return true;
  }
  // No specific matches with buildable URLs — check generic search fallback
  const regionCfg = AFFILIATE_REGIONS[resolvedRegion];
  return !!(regionCfg?.amazon?.tag);
}

// ---------------------------------------------------------------------------
// Internal helpers — exported with __ prefix for unit test access
// ---------------------------------------------------------------------------

/**
 * Slugify a string: lowercase, strip non-alphanumeric characters.
 * Returns 'na' for empty/null/undefined input.
 * @param {*} s
 * @returns {string}
 */
function slugify(s) {
  if (!s) return 'na';
  return String(s).toLowerCase().replace(/[^a-z0-9]/g, '') || 'na';
}

/** 3-letter lowercase English month abbreviations (Jan=1 … Dec=12). */
const MONTH_ABBREVS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Convert a 1-based month integer to 3-letter abbreviation.
 * Returns 'na' for out-of-range or non-integer input.
 * Month is derived from an integer, not user locale (D-14).
 * @param {number} monthInt - 1..12
 * @returns {string}
 */
function monthAbbrev(monthInt) {
  if (!Number.isInteger(monthInt) || monthInt < 1 || monthInt > 12) return 'na';
  return MONTH_ABBREVS[monthInt - 1];
}

/**
 * Classify the current pointing device as 'mobile' (coarse) or 'desktop' (fine).
 * Uses matchMedia '(pointer: coarse)' — two buckets, no UA sniffing (D-08).
 * @returns {'mobile'|'desktop'}
 */
function detectDevice() {
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop';
    }
  } catch (_) {}
  return 'desktop';
}

/**
 * Build the 5-segment SubID string (D-07, D-09, D-14, D-15, D-16).
 * Shape: feature-planttype-task-month-device
 * Missing segments become literal 'na' so the shape is invariant.
 * @param {{ feature, plantType, task, month, device }} ctx
 * @returns {string} e.g. 'supplies-shrub-feed-jun-mobile'
 */
function buildSubId({ feature, plantType, task, month, device } = {}) {
  const f = FEATURE_VALUES.has(feature) ? feature : 'na';
  const p = slugify(plantType);
  const t = slugify(task);
  const m = typeof month === 'number'
    ? monthAbbrev(month)
    : (typeof month === 'string' && month ? slugify(month) : 'na');
  const d = device === 'mobile' || device === 'desktop' ? device : detectDevice();
  return `${f}-${p}-${t}-${m}-${d}`;
}

// Private exports for unit tests (underscore prefix signals internal use)
export const __FALLBACK_TABLE  = FALLBACK_TABLE;
export const __DEFAULT_REGION  = DEFAULT_REGION;
export const __buildSubId      = buildSubId;
export const __slugify         = slugify;
export const __monthAbbrev     = monthAbbrev;
export const __detectDevice    = detectDevice;

// ---------------------------------------------------------------------------
// 13. TROUBLE_KEYWORDS — symptom-token → product-keyword[] resolution (Phase 23 D-11)
// ---------------------------------------------------------------------------

/**
 * TROUBLE_KEYWORDS — symptom-token → product-keyword[] resolution.
 *
 * Bridges the gap between what users *say* (e.g. "yellowing", "wilting",
 * "dying") and the keyword vocabulary on AFFILIATE_PRODUCTS.keywords[]
 * (e.g. "aphid", "mildew", "rose"). Without this, the pitfall-4 case
 * "the plant is wilting" silently returns zero products.
 *
 * Each key MUST be a word from trouble-keywords.js TROUBLE_KEYWORDS.
 * Each value is a list of lowercase tokens we also scan product.keywords[] for.
 * Union with raw text scan preserves catalogue-driven matches.
 *
 * Rationale per mapping:
 *   - 'yellowing' → most common cause is aphid/mildew damage or nutrient
 *     deficiency (ericaceous feed for acid-lovers, chicken manure for general)
 *   - 'wilting'   → water-related but commonly aphid or vine-weevil root
 *     damage, or slug damage on tender plants
 *   - 'dying'     → broad; surface top pest-control products
 *   - 'scale' / 'fungus' → categorical coverage of products that target the family
 */
export const TROUBLE_KEYWORDS = Object.freeze({
  // Original D-11 set
  'yellowing':  ['aphid', 'mildew', 'chicken manure', 'ericaceous'],
  'wilting':    ['aphid', 'vine weevil', 'slug'],
  'aphids':     ['aphid', 'rose pest', 'organic pest'],
  'blackspot':  ['blackspot', 'rose', 'rose disease'],
  'black spot': ['blackspot', 'rose', 'rose disease'],
  'mildew':     ['mildew', 'organic pest'],
  'fungus':     ['mildew', 'blackspot', 'rose disease'],
  'pest':       ['organic pest', 'aphid', 'slug', 'vine weevil', 'rose pest'],
  'dying':      ['aphid', 'mildew', 'slug', 'vine weevil', 'ericaceous'],
  'rust':       ['rust', 'rose disease'],
  'scale':      ['aphid', 'organic pest', 'mildew'],

  // Phase 23 v2 — comprehensive coverage
  // Slugs + snails
  'slugs':            ['slug', 'nematode', 'copper', 'barrier'],
  'slug':             ['slug', 'nematode', 'copper', 'barrier'],
  'snails':           ['slug', 'snail', 'copper', 'barrier'],
  'snail':            ['slug', 'snail', 'copper', 'barrier'],

  // Sap-sucking and flying pests
  'whitefly':         ['whitefly', 'organic pest', 'aphid'],
  'greenfly':         ['aphid', 'rose pest', 'organic pest'],
  'blackfly':         ['aphid', 'rose pest', 'organic pest'],
  'mealybug':         ['mealybug', 'organic pest', 'aphid'],
  'spider mite':      ['red spider mite', 'organic pest'],
  'red spider mite':  ['red spider mite', 'organic pest'],
  'thrip':            ['organic pest', 'aphid'],

  // Beetles and weevils
  'vine weevil':      ['vine weevil', 'weevil', 'container pest'],
  'weevil':           ['vine weevil', 'weevil', 'container pest'],

  // Caterpillars, moths, maggots, grubs
  'caterpillar':      ['caterpillar', 'winter moth', 'grease band'],
  'moth':             ['winter moth', 'codling moth', 'caterpillar', 'grease band'],
  'maggot':           ['codling moth', 'organic pest'],
  'grub':             ['vine weevil', 'weevil'],

  // Fungal and bacterial diseases
  'powdery mildew':   ['mildew', 'organic pest'],
  'blight':           ['mildew', 'organic pest', 'rose disease'],
  'mould':            ['mildew', 'organic pest'],
  'mold':             ['mildew', 'organic pest'],
  'leaf spot':        ['blackspot', 'rose disease', 'mildew'],
  'canker':           ['rose disease'],

  // Visible symptoms — route to the most likely culprits
  'holes in leaves':  ['slug', 'caterpillar', 'winter moth'],
  'eaten':            ['slug', 'caterpillar', 'vine weevil'],
  'chewed':           ['slug', 'caterpillar'],
  'sticky':           ['aphid', 'rose pest'],
  'sooty':            ['aphid', 'mildew'],
  'bugs':             ['organic pest', 'aphid'],
  'insects':          ['organic pest', 'aphid'],
});

// ---------------------------------------------------------------------------
// 14. matchProductsInText — free-text → AFFILIATE_PRODUCTS resolver (Phase 23 D-11)
// ---------------------------------------------------------------------------

/**
 * Match AFFILIATE_PRODUCTS to a free-text note or user message.
 *
 * Used by ONB-05 (notes trigger) and ONB-06 (Ask Tend pre-AI match).
 *
 * Flow:
 *   1. matchesTroubleKeyword(text) → null short-circuits to []
 *   2. Union two product sets:
 *      a) Products whose .keywords[i] is a substring of text (case-insensitive)
 *      b) Products whose .keywords[i] matches a token from
 *         TROUBLE_KEYWORDS[matchedSymptom]
 *   3. Score by total overlapping signal count (desc); tie-break by
 *      catalogue order (stable sort).
 *   4. Caller takes [0] and renders a TroubleProductCard.
 *
 * Region is accepted for future-proofing (regional product filter) but is
 * not consulted in v2.1 — all products are GB-seeded. Kept in the signature
 * so Phase 23 callers pass { region } without later refactor.
 *
 * @param {string} text
 * @param {{region?:string}} [opts]
 * @returns {Array<typeof AFFILIATE_PRODUCTS[0]>}
 */
export function matchProductsInText(text, opts = {}) {
  if (!text || typeof text !== 'string') return [];
  const matchedSymptom = matchesTroubleKeyword(text);
  if (!matchedSymptom) return [];
  const lowerText = text.toLowerCase();
  const mappedTokens = (TROUBLE_KEYWORDS[matchedSymptom] || []).map(t => t.toLowerCase());
  const scored = AFFILIATE_PRODUCTS.map((p, idx) => {
    const kws = (p.keywords || []).map(k => k.toLowerCase());
    const rawHits = kws.filter(kw => lowerText.includes(kw)).length;
    const mappedHits = kws.filter(kw =>
      mappedTokens.some(t => t.includes(kw) || kw.includes(t))
    ).length;
    return { p, idx, score: rawHits + mappedHits };
  }).filter(x => x.score > 0);
  // Stable sort: higher score first; equal scores keep catalogue order.
  scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
  return scored.map(x => x.p);
}

// SYMPTOM_LIST is imported above to keep symptom→product mapping in sync with
// the authoritative trouble-keywords list. Re-exposing for type-narrowing tests.
export const __SYMPTOM_LIST = SYMPTOM_LIST;

// ── Phase 27 ENG-02 — Shopping intent keyword match ─────────────────────────
// Used by AskTend to surface product cards on shopping-intent questions.
// SEPARATE from matchProductsInText (symptom-gated) so the ONB-05 note trigger
// is unaffected. Zero new AI cost. Returns AFFILIATE_PRODUCTS[] ranked by
// keyword-overlap score, stable-sorted by catalogue order.
// Refs: CONTEXT.md D-01, D-02; RESEARCH.md Pattern 1.

export const SHOPPING_KEYWORDS = Object.freeze([
  // feed
  { word: 'feed',             match: 'word'   },
  { word: 'fertiliser',       match: 'any'    },
  { word: 'fertilizer',       match: 'any'    },
  { word: 'bonemeal',         match: 'any'    },
  { word: 'bone meal',        match: 'phrase' },
  { word: 'growmore',         match: 'any'    },
  { word: 'blood fish',       match: 'phrase' },
  { word: 'liquid feed',      match: 'phrase' },
  { word: 'seaweed',          match: 'any'    },
  { word: 'tomato feed',      match: 'phrase' },
  { word: 'rose feed',        match: 'phrase' },
  { word: 'ericaceous',       match: 'any'    },
  { word: 'potash',           match: 'any'    },
  { word: 'nitrogen',         match: 'any'    },
  // mulch
  { word: 'mulch',            match: 'word'   },
  { word: 'compost',          match: 'word'   },
  { word: 'manure',           match: 'any'    },
  { word: 'bark',             match: 'word'   },
  { word: 'top dress',        match: 'phrase' },
  { word: 'top dressing',     match: 'phrase' },
  { word: 'leaf mould',       match: 'phrase' },
  { word: 'mushroom compost', match: 'phrase' },
  { word: 'well-rotted',      match: 'phrase' },
  // pest (shopping-intent, e.g. "where do I buy slug pellets")
  { word: 'pellets',          match: 'any'    },
  { word: 'nematodes',        match: 'any'    },
  { word: 'trap',             match: 'word'   },
  { word: 'spray',            match: 'word'   },
  { word: 'fungicide',        match: 'any'    },
  // tool
  { word: 'secateurs',        match: 'any'    },
  { word: 'shears',           match: 'word'   },
  { word: 'gloves',           match: 'word'   },
  { word: 'twine',            match: 'word'   },
  { word: 'canes',            match: 'word'   },
  { word: 'watering can',     match: 'phrase' },
  { word: 'trowel',           match: 'word'   },
  { word: 'fork',             match: 'word'   },
  { word: 'spade',            match: 'word'   },
  { word: 'grow bag',         match: 'phrase' },
  { word: 'labels',           match: 'word'   },
  { word: 'pots',             match: 'word'   },
]);

/**
 * matchesShoppingKeyword(text) — returns the first matching shopping word, or null.
 * Uses word-boundary regex for 'word' matchers (prevents "feeding" from hitting "feed").
 * Uses case-insensitive substring includes for 'phrase' and 'any'.
 */
export function matchesShoppingKeyword(text) {
  if (!text || typeof text !== 'string') return null;
  const lower = text.toLowerCase();
  for (const { word, match } of SHOPPING_KEYWORDS) {
    if (match === 'word') {
      // Escape is unnecessary — all words are [a-z ] only.
      const re = new RegExp(`\\b${word}\\b`, 'i');
      if (re.test(text)) return word;
    } else {
      if (lower.includes(word.toLowerCase())) return word;
    }
  }
  return null;
}

// Maps shopping keyword words to AFFILIATE_PRODUCTS taskTypes for category scoring.
// When "fertiliser" matches, feed-category products receive a baseline score of 1
// so they surface even when the text doesn't contain a product keyword verbatim.
const SHOPPING_KEYWORD_TASKTYPE = Object.freeze({
  // feed category
  feed:             'feed',
  fertiliser:       'feed',
  fertilizer:       'feed',
  bonemeal:         'feed',
  'bone meal':      'feed',
  growmore:         'feed',
  'blood fish':     'feed',
  'liquid feed':    'feed',
  seaweed:          'feed',
  'tomato feed':    'feed',
  'rose feed':      'feed',
  ericaceous:       'feed',
  potash:           'feed',
  nitrogen:         'feed',
  // mulch category
  mulch:            'mulch',
  compost:          'mulch',
  manure:           'mulch',
  bark:             'mulch',
  'top dress':      'mulch',
  'top dressing':   'mulch',
  'leaf mould':     'mulch',
  'mushroom compost': 'mulch',
  'well-rotted':    'mulch',
  // pest category (shopping-intent)
  pellets:          'other',
  nematodes:        'other',
  trap:             'pest',
  spray:            'pest',
  fungicide:        'pest',
  // tool category
  secateurs:        'tool',
  shears:           'tool',
  gloves:           'tool',
  twine:            'tool',
  canes:            'tool',
  'watering can':   'tool',
  trowel:           'tool',
  fork:             'tool',
  spade:            'tool',
  'grow bag':       'tool',
  labels:           'tool',
  pots:             'tool',
});

/**
 * matchShoppingProductsInText(text, opts) — shopping-intent counterpart of
 * matchProductsInText. Does NOT gate on matchesTroubleKeyword.
 * Returns [] when no shopping keyword is detected (deterministic silence).
 *
 * Scoring: each product earns points for:
 *   a) Its own keywords appearing verbatim in the user's text (rawHits)
 *   b) Its taskType matching the category of the detected shopping keyword (categoryHit=1)
 * Category scoring ensures generic queries like "what fertiliser?" surface feed
 * products even when no product keyword appears verbatim in the text.
 */
export function matchShoppingProductsInText(text, opts = {}) {
  if (!text || typeof text !== 'string') return [];
  const matchedWord = matchesShoppingKeyword(text);
  if (!matchedWord) return [];
  const lowerText = text.toLowerCase();
  const matchedTaskType = SHOPPING_KEYWORD_TASKTYPE[matchedWord] || null;
  const scored = AFFILIATE_PRODUCTS.map((p, idx) => {
    const kws = (p.keywords || []).map(k => k.toLowerCase());
    const rawHits = kws.filter(kw => lowerText.includes(kw)).length;
    // Category hit: 1 point if this product's taskTypes include the matched category
    const categoryHit = matchedTaskType && (p.taskTypes || []).includes(matchedTaskType) ? 1 : 0;
    return { p, idx, score: rawHits + categoryHit };
  }).filter(x => x.score > 0);
  // Stable sort: higher score first; equal score keeps catalogue order.
  scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
  return scored.map(x => x.p);
}

/**
 * inventoryFilter(product, inventory) — returns true when a product should be
 * SUPPRESSED from the Ask Tend stack because the user has marked its
 * inventory entry as stock:'ok'. `productRuleKey: null` is a no-op
 * (tools not tracked in shed). Ref: CONTEXT.md D-05, D-06.
 */
export function inventoryFilter(product, inventory) {
  if (!product) return false;
  const key = product.productRuleKey;
  if (!key) return false;
  const entry = inventory && inventory[key];
  if (!entry) return false;
  return entry.stock === 'ok';
}
